import cx from "classnames";
import React from "react";
import { connect } from "react-redux";

import { blogsPostDetailUrl, blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import { groupsPhotoDetailUrl, groupsPostDetailUrl } from "@/mk2/apps/groups/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Dot } from "@/mk2/components/Dot";
import { Link } from "@/mk2/components/Link";
import {
    ComplaintEntity,
    ComplaintTargetType,
    ComplaintWithoutTargetEntity,
    ComplaintWithoutTargetSchema,
} from "@/mk2/containers/Complaint/Complaint.schemas";
import { complaintsDelete } from "@/mk2/containers/Complaints/Complaints.actions";
import saga from "@/mk2/containers/Complaints/Complaints.sagas";
import {
    findComplaintsNonDeleted,
    getComplaintsNonDeleted,
} from "@/mk2/containers/Complaints/Complaints.selectors";
import registerInStore from "@/mk2/decorators/registerInStore";
import { cacheLast } from "@/mk2/helpers/cache";
import { timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import { AppState } from "@/mk2/reducers";
import { BlogPostEntity } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import {
    BLOG_COMMENT_REMOVE_COMPLAINT,
    COMMENT,
    COMPLAINTS_REPORTED_BY,
    IMAGE,
    IMAGE_REMOVE_COMPLAINTS,
    POST_COMPLAINTS_SUBTREE,
    POST_REMOVE_COMPLAINTS_ALSO_POST_SUBTREE_COMPLAINTS,
    POST_SUBTREE_REMOVE_COMPLAINTS,
} from "@/mk/autogenerated/translation";

import styles from "./Complaints.mscss";

interface OwnProps {
    className?: string;
    id: number;
    entity: ComplaintTargetType;
    deleteLabel: string;
    header?: React.ReactNode;
    postSubtreeComplaintsData?: {
        complaintIds: number[];
        parentPost: BlogPostEntity;
    };
    adminTheme?: boolean;
}

interface StateProps {
    complaints: ComplaintEntity[];
    postSubtreeComplaints?: ComplaintWithoutTargetEntity[];
}

interface DispatchProps {
    onDeleteComplaint(entityType: ComplaintTargetType, entityId: number, complaintId: number);
    onDeleteComplaints(entityType: ComplaintTargetType, entityId: number);
    onDeleteComplaintSubtree(entityType: ComplaintTargetType, entityId: number);
}

type Props = OwnProps & StateProps & DispatchProps;

class Complaints extends React.Component<Props> {
    private onDeleteComplaintCache = cacheLast();
    private onDeleteComplaintsCache = cacheLast();
    private onDeleteComplaintSubtreeCache = cacheLast();

    public render() {
        const {
            adminTheme,
            className,
            header,
            complaints,
            deleteLabel,
            entity,
            id,
            onDeleteComplaint,
            onDeleteComplaints,
            onDeleteComplaintSubtree,
            postSubtreeComplaints,
            postSubtreeComplaintsData,
        } = this.props;

        return (complaints && complaints.length > 0) ||
            (postSubtreeComplaints && postSubtreeComplaints.length > 0) ? (
            <div className={cx(styles.Complaints, className)}>
                {header}
                {complaints && complaints.length > 0 && (
                    <>
                        {complaints.map((complaint) => (
                            <div
                                key={complaint.id}
                                className={cx(
                                    styles.Complaints__item,
                                    adminTheme && styles["Complaints__item--admin"],
                                )}
                            >
                                <div className={styles.Complaints__item__meta}>
                                    {timeAt(complaint.createdAt)} {COMPLAINTS_REPORTED_BY}{" "}
                                    <Link
                                        to={url(blogsProfileUrl, {
                                            username: complaint.user.username,
                                        })}
                                        className={styles.Complaints__item__meta__link}
                                    >
                                        {complaint.user.username}
                                    </Link>
                                </div>
                                <div className={styles.Complaints__item__content}>
                                    {complaint.comment}
                                </div>
                            </div>
                        ))}
                        <Btn
                            type={adminTheme ? BtnType.AdminTextLight : BtnType.ModeratorTextLight}
                            onClick={this.onDeleteComplaintsCache(
                                () => {
                                    onDeleteComplaints(entity, id);
                                },
                                entity,
                                id,
                            )}
                            label={deleteLabel}
                        />
                    </>
                )}
                {postSubtreeComplaints && postSubtreeComplaints.length > 0 && (
                    <>
                        <h4>{POST_COMPLAINTS_SUBTREE}:</h4>
                        {postSubtreeComplaints.map((complaint) => {
                            const post = postSubtreeComplaintsData.parentPost;

                            const targetUrl = complaint.imageId
                                ? post.group
                                    ? url(groupsPhotoDetailUrl, {
                                          groupId: post.group.id,
                                          postSlug: post.slug,
                                          imageId: complaint.imageId,
                                      })
                                    : url(blogsPostDetailUrl(post.type, complaint.imageId), {
                                          username: post.author.username,
                                          postSlug: post.slug,
                                          imageId: complaint.imageId,
                                      })
                                : post.group
                                  ? url(groupsPostDetailUrl, {
                                        groupId: post.group.id,
                                        postSlug: post.slug,
                                    })
                                  : url(blogsPostDetailUrl(post.type), {
                                        username: post.author.username,
                                        postSlug: post.slug,
                                    });

                            return (
                                <div
                                    key={complaint.id}
                                    className={cx(
                                        styles.Complaints__item,
                                        adminTheme && styles["Complaints__item--admin"],
                                    )}
                                >
                                    <div className={styles.Complaints__item__meta}>
                                        {timeAt(complaint.createdAt)} {COMPLAINTS_REPORTED_BY}{" "}
                                        <Link
                                            to={url(blogsProfileUrl, {
                                                username: complaint.user.username,
                                            })}
                                            className={styles.Complaints__item__meta__link}
                                        >
                                            {complaint.user.username}
                                        </Link>
                                    </div>
                                    <div className={styles.Complaints__item__content}>
                                        {complaint.comment}
                                    </div>
                                    {complaint.target &&
                                        complaint.target.schema ===
                                            ComplaintTargetType.PhotoblogComment && (
                                            <>
                                                <Btn
                                                    type={
                                                        adminTheme
                                                            ? BtnType.AdminTextLight
                                                            : BtnType.ModeratorTextLight
                                                    }
                                                    link={`${targetUrl}#comment-${complaint.target.id}`}
                                                    label={COMMENT}
                                                />
                                                <Dot spaces={0} />
                                                <Btn
                                                    type={
                                                        adminTheme
                                                            ? BtnType.AdminTextLight
                                                            : BtnType.ModeratorTextLight
                                                    }
                                                    onClick={this.onDeleteComplaintCache(
                                                        () => {
                                                            onDeleteComplaint(
                                                                complaint.target.schema,
                                                                complaint.target.id,
                                                                complaint.id,
                                                            );
                                                        },
                                                        complaint.target.schema,
                                                        complaint.target.id,
                                                        complaint.id,
                                                    )}
                                                    label={BLOG_COMMENT_REMOVE_COMPLAINT}
                                                />
                                            </>
                                        )}
                                    {complaint.target &&
                                        complaint.target.schema ===
                                            ComplaintTargetType.PhotoblogImage && (
                                            <>
                                                <Btn
                                                    type={
                                                        adminTheme
                                                            ? BtnType.AdminTextLight
                                                            : BtnType.ModeratorTextLight
                                                    }
                                                    link={`${targetUrl}`}
                                                    label={IMAGE}
                                                />
                                                <Dot spaces={0} />
                                                <Btn
                                                    type={
                                                        adminTheme
                                                            ? BtnType.AdminTextLight
                                                            : BtnType.ModeratorTextLight
                                                    }
                                                    onClick={this.onDeleteComplaintCache(
                                                        () => {
                                                            onDeleteComplaint(
                                                                complaint.target.schema,
                                                                complaint.target.id,
                                                                complaint.id,
                                                            );
                                                        },
                                                        complaint.target.schema,
                                                        complaint.target.id,
                                                        complaint.id,
                                                    )}
                                                    label={IMAGE_REMOVE_COMPLAINTS}
                                                />
                                            </>
                                        )}
                                </div>
                            );
                        })}
                    </>
                )}
                {complaints && complaints.length > 0
                    ? postSubtreeComplaints &&
                      postSubtreeComplaints.length > 2 && (
                          <div>
                              <Btn
                                  type={
                                      adminTheme
                                          ? BtnType.AdminTextLight
                                          : BtnType.ModeratorTextLight
                                  }
                                  onClick={this.onDeleteComplaintSubtreeCache(
                                      () => {
                                          onDeleteComplaintSubtree(entity, id);
                                      },
                                      entity,
                                      id,
                                  )}
                                  label={POST_REMOVE_COMPLAINTS_ALSO_POST_SUBTREE_COMPLAINTS}
                              />
                          </div>
                      )
                    : postSubtreeComplaints &&
                      postSubtreeComplaints.length > 2 && (
                          <div>
                              <Btn
                                  type={
                                      adminTheme
                                          ? BtnType.AdminTextLight
                                          : BtnType.ModeratorTextLight
                                  }
                                  onClick={this.onDeleteComplaintSubtreeCache(
                                      () => {
                                          onDeleteComplaintSubtree(entity, id);
                                      },
                                      entity,
                                      id,
                                  )}
                                  label={POST_SUBTREE_REMOVE_COMPLAINTS}
                              />
                          </div>
                      )}
            </div>
        ) : null;
    }
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    return {
        complaints: findComplaintsNonDeleted(state, ownProps.entity, ownProps.id),
        postSubtreeComplaints: ownProps.postSubtreeComplaintsData
            ? getComplaintsNonDeleted(
                  getDenormalizedEntities<ComplaintWithoutTargetEntity>(
                      state,
                      ComplaintWithoutTargetSchema,
                      ownProps.postSubtreeComplaintsData.complaintIds,
                  ),
              )
            : [],
    };
}

function mapDispatchToProps(dispatch, ownProps: OwnProps): DispatchProps {
    return {
        onDeleteComplaint: (
            entityType: ComplaintTargetType,
            entityId: number,
            complaintId: number,
        ) => dispatch(complaintsDelete(entityType, entityId, complaintId)),
        onDeleteComplaints: (entityType: ComplaintTargetType, entityId: number) =>
            dispatch(complaintsDelete(entityType, entityId)),
        onDeleteComplaintSubtree: (entityType: ComplaintTargetType, entityId: number) =>
            dispatch(complaintsDelete(entityType, entityId, null, true)),
    };
}

export default registerInStore<OwnProps>((store) => {
    store.injectSaga("containers.global.complaints", saga);
})(connect(mapStateToProps, mapDispatchToProps)(Complaints));
